import React, { useState, useEffect, useMemo, useCallback } from "react";
import { IssueForm } from "./IssueListComps/IssueForm";
import { Container, Row, Col } from "react-bootstrap";
import MaterialTable from "material-table";
import { ISSUECOLS, ALLISSUECOLS } from "./../../util/columns";
import GYAxios from "./../../util/GYAxios";
import Icon from "@material-ui/core/Icon";
import Noti from "../../util/noti";
import { useUser } from "../../util/user";
import { checkIfArray, checkIfObjectHasKeys } from "./../../util/checking";
import { dateTimeFormat, dateTimeFormatCompare } from "./../../util/formatter";
import LoaderGraph from "../general/LoaderGraph";
import dividePages from "../../util/dividePages";
import Select from 'react-select';
import { localTimeToPickerTime, pickerTimeTolocalTime } from "../../util/momentTimezone";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import styled, { css, createGlobalStyle } from 'styled-components';
import moment from "../../util/momentTimezone";

function IssueList() {
	const user = useUser();
	const kcID = user.sub;
	const [userInfo, setUserInfo] = useState({});

	const allCols = useMemo(() => ALLISSUECOLS, []);
	const siteCols = useMemo(() => ISSUECOLS, []);
	const [data, setData] = useState([]);
	const [tableAction, setTableAction] = useState([]);
	const [cols, setCols] = useState([]);
	const [title, setTitle] = useState("");
	const [ready, setReady] = useState(false);
	const [clickedRow, setClickedRow] = useState(null);

	const [selectedCountry, setSelectedCountry] = useState(null);
	const [countryList, setCountryList] = useState([]);

	const [selectedSite, setSelectedSite] = useState(null);
	const [siteList, setSiteList] = useState([]);
	const siteListFiltered = useMemo(() => siteList.filter(siteOption => {
		if (!selectedCountry || selectedCountry.value === 'ALL') { return true }
		const country = selectedCountry.value;
		return siteOption.options.find(site => site.country === country);
	}), [selectedCountry, siteList]);

	const [openForm, setOpenForm] = useState(false);
	const [formID, setFormID] = useState(null);

	const [all, setAll] = useState(false);
	const [unsolved, setUnsolved] = useState(true);

	const [noOfPages, setNoOfPages] = useState([1]);

	const [startDate, setStartDate] = useState(localTimeToPickerTime());
	const [endDate, setEndDate] = useState(null);

	const handleChange = (dates) => {
		const [newStartDate, newEndDate] = dates;
		setStartDate(newStartDate ? pickerTimeTolocalTime(newStartDate) : null);
		setEndDate(newEndDate ? pickerTimeTolocalTime(newEndDate) : null);
	};

	const DatePickerInputStyles = createGlobalStyle`
		.react-datepicker__input-container input {
			font-size: 1em;
			padding: 0.5rem;
			text-align: center;
		}
	`;

	function switchButtons(bool) {
		setAll(bool);
		setUnsolved(!bool);
	}

	function reloadReport() {
		if (all || unsolved) {
			const command = all ? "all" : "unsolved";
			onClickBigButtons(command);
		} else if (selectedSite && checkIfObjectHasKeys(selectedSite)) {
			const tempSite = selectedSite;
			setSelectedSite(null);
			setSelectedSite(tempSite);
		} else {
			Noti.create({
				type: "e",
				msg: "Something is wrong.",
			});
			setReady(true);
		}
	}

	const fullTableActions = useCallback(() => {
		return [
			{
				icon: "edit",
				tooltip: "Edit Issue",
				onClick: (e, rowData) => editIssue(rowData),
			},
			(rowData) => {
				return rowData?.actions.length > 0 && rowData?.status === "Pending"
					? {
						icon: "add",
						tooltip: "Add Action",
						onClick: (e, rowData) => addAction(rowData),
					}
					: null;
			},
			userInfo?.dept_id === 3 &&
			userInfo?.type === 1 && {
				icon: "delete",
				tooltip: "Delete Issue",
				onClick: (e, rowData) => deleteIssueRow(rowData),
			},
		];
	});

	const siteTableAction = useCallback(() => {
		return [
			{
				icon: "edit",
				tooltip: "Edit Issue",
				onClick: (e, rowData) => editIssue(rowData),
			},
			(rowData) => {
				return rowData?.actions.length > 0 && rowData?.status === "Pending"
					? {
						icon: "add",
						tooltip: "Add Action",
						onClick: (e, rowData) => addAction(rowData),
					}
					: null;
			},
			userInfo?.dept_id === 3 &&
			userInfo?.type === 1 && {
				icon: "delete",
				tooltip: "Delete Issue",
				onClick: (e, rowData) => deleteIssueRow(rowData),
			},
			{
				icon: "add_circle",
				tooltip: "Add New Issue",
				isFreeAction: true,
				onClick: () => addIssue(),
			},
		];
	});

	const actionsDetailPanel = useCallback(
		(row) => {
			row.actions.sort(function (a, b) {
				const aDate = dateTimeFormat(a.action_date);
				const bDate = dateTimeFormat(b.action_date);
				if (aDate < bDate) return -1;
				if (aDate > bDate) return 1;
				return 0;
			});

			return row.actions.map((v) => {
				return (
					<Row style={{ padding: "10px 10px 10px 30px" }} key={v.id}>
						<Col sm={3} style={{ textAlign: "right" }}>
							{dateTimeFormat(v.action_date)}
						</Col>
						<Col sm={6}>{v.gy_action}</Col>
						<Col sm={3} style={{ display: "flex" }}>
							<Icon
								className="btn-issue-report"
								onClick={() => editAction(v, row.tableData.id)}
							>
								edit
							</Icon>
							{(row.status === 0 || row.status === "Pending") && (
								<Icon
									className="btn-issue-report"
									onClick={() => deleteAction(v, row.tableData.id)}
								>
									delete
								</Icon>
							)}
						</Col>
					</Row>
				);
			});
		},
		[data]
	);

	function onChangeSite(site) {
		if (checkIfObjectHasKeys(site)) {
			setSelectedSite(site);
			setUnsolved(false);
			setAll(false);
		} else {
			Noti.create({
				type: "e",
				msg: "Something is wrong. Please contact IT department!",
			});
			return;
		}
	}

	function addIssue() {
		if (!selectedSite) { return }
		setOpenForm(true);
		setFormID({ siteID: selectedSite.value });
	}

	function editIssue(rowData) {
		const issue = rowData;
		setClickedRow(issue.tableData.id);
		setOpenForm(true);
		setFormID({ issueID: issue.issue_id });
	}

	function deleteIssueRow(rowData) {
		const issue = rowData;
		setClickedRow(issue.tableData.id);
		const question = `Are you sure that you want to delete "${issue.gy_issue}" issue happened at "${issue.issue_date}" and its actions (if any)?`;

		if (window.confirm(question)) {
			deleteData(`/issue/${issue.issue_id}`).then(function (deletedIssueRes) {
				if (checkIfObjectHasKeys(deletedIssueRes)) {
					Noti.create({
						type: "s",
						msg: "Deleting selected issue is successful.",
					});

					if (checkIfArray(issue.actions)) {
						if (issue.actions.length > 0) {
							deleteData(`/actions/issue/${issue.issue_id}`).then(function (
								deletedActionRes
							) {
								reloadReport();

								if (checkIfObjectHasKeys(deletedActionRes)) {
									Noti.create({
										type: "s",
										msg: "Deleting action(s) of selected issue is successful.",
									});
								} else {
									Noti.create({
										type: "w",
										msg: "Cannot delete action(s) of selected issue",
									});
								}
							});
						} else reloadReport();
					} else reloadReport();
				} else {
					Noti.create({
						type: "e",
						msg: "Cannot delete selected issue. \nPlease try again or Contact IT Dep.",
					});
				}
			});
		}
	}

	function addAction(rowData) {
		const issue = rowData;
		setClickedRow(issue.tableData.id);
		setOpenForm(true);
		setFormID({
			actionID: {
				issueID: issue.issue_id,
				actionID: null,
			},
		});
	}

	function editAction(data, rowID) {
		const action = data;
		setClickedRow(rowID);
		setOpenForm(true);
		const actionID = action.id;

		setFormID({
			actionID: {
				issueID: action.issue_id,
				actionID: actionID,
			},
		});
	}

	function deleteAction(data, rowID) {
		const action = data;
		setClickedRow(rowID);
		const question = `Are you sure that you want to delete "${action.gy_action
			}" action happened at "${dateTimeFormat(action.action_date)}"?`;

		if (window.confirm(question)) {
			deleteData(`/actions/${action.id}`).then(function (deletedActionRes) {
				if (checkIfObjectHasKeys(deletedActionRes)) {
					Noti.create({
						type: "s",
						msg: "Deleting selected action is successful.",
					});

					reloadReport();
				} else {
					Noti.create({
						type: "e",
						msg: "Cannot delete selected action. \nPlease try again or Contact IT Dep.",
					});
				}
			});
		}
	}

	function checkLatestDate(assumedLatest, inputDate) {
		if (
			dateTimeFormatCompare(assumedLatest) < dateTimeFormatCompare(inputDate)
		) {
			return dateTimeFormat(inputDate);
		} else {
			return dateTimeFormat(assumedLatest);
		}
	}

	function processingData(issue, action) {
		const tempIssue = issue;
		const tempAction = action;

		if (!checkIfObjectHasKeys(tempIssue) || !checkIfArray(tempAction)) {
			Noti.create({
				type: "e",
				msg: "Cannot processing data.",
			});
			return null;
		}

		tempIssue.status = tempIssue.status === 0 ? "Pending" : "Solved";
		tempIssue.issue_date = dateTimeFormat(tempIssue.issue_date);
		if (tempAction.length > 0) {
			let latestActionDate = tempIssue.issue_date;
			const eachAction = tempAction;
			eachAction.forEach((v) => {
				latestActionDate = checkLatestDate(latestActionDate, v.action_date);
			});

			tempIssue.action_date = latestActionDate;
			const latestAction = eachAction.filter(
				(v) => dateTimeFormat(v?.action_date) === latestActionDate
			);
			tempIssue.gy_action = latestAction[0]?.gy_action;
			tempIssue.actions = eachAction;
		} else {
			tempIssue.actions = [];
		}

		return tempIssue;
	}

	function onClickBigButtons(command) {
		if (!selectedCountry) { return }
		setSelectedSite(null);
		setReady(false);
		setTableAction([]);
		setData([]);
		setCols(allCols);

		if (command === "all") {
			switchButtons(true);
			const uri = `/issue/opt/${command}?country=${selectedCountry.value}&start=${moment(startDate).startOf("month").format("YYYY-MM-DD")}&end=${moment(endDate).endOf("month").format("YYYY-MM-DD")}`;
			let title = "";
			if (selectedCountry.value === "ALL") title = "ALL COUNTRY ";
			else title = `${selectedCountry.label.toUpperCase()} `;
			title += "ISSUES REPORTED";
			setTitle(title);
			setReady(true);
			GYAxios.get(uri).then(res => {
				if (res.status != 200) {
					Noti.create({
						type: "e",
						msg: "Something is wrong.",
					});
					console.log(res);
				} else {
					if (checkIfArray(res.data)) {
						const data = res.data.map(issue => {
							issue.issue_date = dateTimeFormat(issue.issue_date);
							issue.status = issue.status === 0 ? "Pending" : "Solved";
							issue.action_date = issue.action_date ? dateTimeFormat(issue.action_date) : "";
							issue.actions = [
								{
									action_date: issue.action_date,
									gy_action: issue.gy_action,
								}
							];
							return issue;
						});

						setTableAction(fullTableActions);
						setData(data);
						setReady(true);
					}
				}
			});
		} else if (command === "unsolved") {
			const uri = `/issue/opt/${command}?country=${selectedCountry.value}`;
			switchButtons(false);
			let title = "";
			if (selectedCountry.value === "ALL") title = "ALL COUNTRY ";
			else title = `${selectedCountry.label.toUpperCase()} `;
			title += "UNSOLVED ISSUES REPORTED";
			setTitle(title);

			fetchIssuesActions(uri).then(function (res) {
				setTableAction(fullTableActions);

				if (res === null) {
					Noti.create({
						type: "e",
						msg: "No issue is detected.",
					});
					setReady(true);
				}
			});
		} else {
			Noti.create({
				type: "w",
				msg: "Button is broken.",
			});

			setReady(true);
		}

		/* if (command === "all" || command === "unsolved") {
			const uri = `/issue/opt/${command}?country=${selectedCountry.value}&start=${moment(startDate).startOf("month").format("YYYY-MM-DD")}&end=${moment(endDate).endOf("month").format("YYYY-MM-DD")}`;
			if (command === "all") switchButtons(true);
			if (command === "unsolved") switchButtons(false);
			let title = "";
			if (selectedCountry.value === "ALL") title = "ALL COUNTRY ";
			else title = `${selectedCountry.label.toUpperCase()} `;
			if (command === "all") title += "ISSUES REPORTED";
			else title += "UNSOLVED ISSUES REPORTED";
			setTitle(title);

			fetchIssuesActions(uri).then(function (res) {
				setTableAction(fullTableActions);

				if (res === null) {
					Noti.create({
						type: "e",
						msg: "No issue is detected.",
					});
					setReady(true);
				}
			});
		} else {
			Noti.create({
				type: "w",
				msg: "Button is broken.",
			});

			setReady(true);
		} */
	}

	async function fetchData(uri) {
		return await GYAxios.get(uri)
			.then((res) => {
				try {
					if (res.data.error !== undefined) {
						console.log(res.data.error);
						return null;
					} else {
						if (checkIfArray(res.data)) {
							return res.data;
						} else {
							console.log(res.data.error);
							return null;
						}
					}
				} catch (e) {
					console.log(e);
					setReady(true);
					return null;
				}
			})
			.catch((err) => {
				console.log(err);
				Noti.create({
					type: "e",
					msg: "Process is timeout.",
				});
				setReady(true);
				return null;
			});
	}

	async function deleteData(uri) {
		return await GYAxios.delete(uri)
			.then((res) => {
				if (res.data?.affectedRows > 0) return res.data;
				else {
					if (res.data.error !== undefined) console.log(res.data.error);
					return null;
				}
			})
			.catch((err) => {
				console.log(err);
				Noti.create({
					type: "e",
					msg: "Process is timeout.",
				});
				return null;
			});
	}

	const fetchIssuesActions = useCallback(async (uri) => {
		return await fetchData(uri)
			.then(function (fetchedIssues) {
				if (checkIfArray(fetchedIssues)) {
					if (fetchedIssues.length > 0) {
						const issue_ids = fetchedIssues.map((v) => v.issue_id);
						fetchData(`/actions/issues/${issue_ids.toString()}`).then(function (
							fetchedActions
						) {
							if (checkIfArray(fetchedActions)) {
								const tempIssue = fetchedIssues.map((v) => {
									const tempIssueAction = fetchedActions.filter(
										(x) => v.issue_id === x?.issue_id
									);

									return processingData(v, tempIssueAction);
								});

								setData(tempIssue);
								setReady(true);
								return true;
							} else {
								return null;
							}
						});
					} else {
						setData([]);
						setReady(true);
						return true;
					}
				} else {
					return null;
				}
			})
			.catch((err) => {
				console.log(err);
				Noti.create({
					type: "e",
					msg: "Process is timeout.",
				});
				setReady(true);
				return null;
			});
	});

	useEffect(() => {
		const getSite = () => {
			GYAxios.get("/issue/sites")
				.then((res) => {
					if (res.data.error !== undefined) {
						Noti.create({
							type: "e",
							msg: "No site is detected.",
						});
						return;
					} else {
						setSiteList(res.data);
					}
				})
				.catch((err) => {
					console.log(err);
					setReady(true);
				});
		};

		const getUserInfo = () => {
			GYAxios.get(`/users/${kcID}/init/signin`).then((res) => {
				if (res.data.error !== undefined) {
					Noti.create({
						type: "e",
						msg: "No user info is detected.",
					});
					return;
				} else {
					if (Array.isArray(res.data)) {
						if (res.data.length === 1) {
							setUserInfo(res.data[0]);
						} else {
							Noti.create({
								type: "e",
								msg: "No user info is detected.",
							});
							return;
						}
					} else {
						Noti.create({
							type: "e",
							msg: "No user info is detected.",
						});
						return;
					}
				}
			})
				.catch((err) => {
					console.log(err);
					setUserInfo({});
					setReady(true);
				});
		};


		if (kcID !== null && kcID !== "") {
			getSite();
			getUserInfo();
			setTitle(`ALL COUNTRY UNSOLVED ISSUES REPORTED`);
			setCols(allCols);
			setEndDate(localTimeToPickerTime());
		} else {
			Noti.create({
				type: "e",
				msg: "User ID is not detected!",
			});
			setReady(true);
		}
	}, [kcID]);

	useEffect(() => {
		setSelectedSite(null);
		if (selectedCountry) {
			const uri = `/issue/opt/unsolved?country=${selectedCountry.value}`;
			fetchIssuesActions(uri).then(function (res) {
				let title = "";
				if (selectedCountry.value === "ALL") title = "ALL COUNTRY ";
				else title = `${selectedCountry.label.toUpperCase()} `;
				title += "UNSOLVED ISSUES REPORTED";
				setTitle(title);
				setTableAction(fullTableActions);
				setCols(allCols);
				switchButtons(false);
				if (res === null) {
					Noti.create({
						type: "e",
						msg: "No issue is detected.",
					});
					setReady(true);
				}
			});
		}
	}, [selectedCountry]);

	useEffect(() => {
		if (selectedSite && checkIfObjectHasKeys(selectedSite)) {
			setTableAction([]);
			setReady(false);
			setData([]);
			setTitle(`${selectedSite.label.toUpperCase()} ISSUE REPORTED`);
			const uri = `/issue/filter/filter?column=issue.site_id&value=${selectedSite.value}`;
			fetchIssuesActions(uri).then(function (res) {
				setTableAction(siteTableAction);
				setCols(siteCols);
				if (res === null) {
					Noti.create({
						type: "e",
						msg: "No issue is detected.",
					});
					setReady(true);
				}
			});
		}
	}, [selectedSite]);

	useEffect(() => {
		if (Array.isArray(data)) {
			if (data.length > 0) { // example: data.length = 101
				dividePages(data.length, 15) === null ? Noti.create({
					type: "e",
					msg: "Parameter for dividing pages is mismatch!",
				}) : setNoOfPages(dividePages(data.length, 15));
			}
		}
	}, [data]);

	const getDisplayCountries = useCallback(roles => {
		const displayCountries = [];
		if ((roles.find(role => role === '_GY:TH') && roles.find(role => role === '_GY:O&M')) || roles.find(role => role === '_GY:Management' || role === '_GY:IT')) { displayCountries.push('TH') }
		if ((roles.find(role => role === '_GY:VN') && roles.find(role => role === '_GY:O&M')) || roles.find(role => role === '_GY:Management' || role === '_GY:IT')) { displayCountries.push('VN') }
		if ((roles.find(role => role === '_GY:KH') && roles.find(role => role === '_GY:O&M')) || roles.find(role => role === '_GY:Management' || role === '_GY:IT')) { displayCountries.push('KH') }
		return displayCountries
	}, []);

	const filterOptions = ({ data }, input) => {
		input = input.trim();
		if (input) {
			return data.label.toLowerCase().includes(input.toLowerCase()) || data.client.client_name.toLowerCase().includes(input.toLowerCase());
		}
		return true;
	};

	// get country list from API
	useEffect(() => {
		if (!user) { return }
		GYAxios.get('/select/countries').then(res => {
			const countryOptions = res.data; // The response also includes 'ALL' option and filter out 'KH' option
			setCountryList(countryOptions);
			const displayCountries = getDisplayCountries(user.roles);
			if (displayCountries.length > 1) {
				setSelectedCountry(countryOptions.find(option => option.value === 'ALL'));
			} else if (displayCountries.length === 1) {
				setSelectedCountry(countryOptions.find(option => option.value === displayCountries[0]));
			} else {
				setSelectedCountry(countryOptions.find(option => option.value === 'ALL'));
			}
		});
	}, [user, getDisplayCountries]);

	const onChangeCountry = useCallback(option => {
		setSelectedCountry(option);
		let title = "";
		if (option.value === "ALL") title = "ALL COUNTRY ";
		else title = `${option.label.toUpperCase()} `;
		if (unsolved) title += "UNSOLVED ISSUES REPORTED";
		else title += "ISSUES REPORTED";
		setTitle(title);
	}, []);

	useEffect(() => {
		if (startDate && endDate) {
			onClickBigButtons("all");
		}
	}, [startDate, endDate]);

	return (
		<>
			<Container fluid>
				<Row style={{ marginTop: "5px" }}>
					<Col sm={2}>
						<Select
							options={countryList}
							value={selectedCountry}
							onChange={onChangeCountry}
							styles={{ container: provided => ({ ...provided, width: "100%" }) }}
							placeholder="Select country"
						/>
					</Col>

					<Col sm={4} style={{ paddingLeft: "5px" }}>
						<Select
							onChange={onChangeSite}
							value={selectedSite}
							options={siteListFiltered}
							placeholder={"Select a site"}
							filterOption={filterOptions}
							styles={{ container: provided => ({ ...provided, width: "100%" }) }}
						/>
					</Col>

					<Col sm={6}>
						<div style={{ float: "right" }}>
							<button
								className="btn btn-success"
								style={{ marginRight: "10px" }}
								disabled={unsolved}
								onClick={() => onClickBigButtons("unsolved")}
							>
								Show only unsolved issues
							</button>
							<button
								className="btn btn-success"
								disabled={all}
								onClick={() => onClickBigButtons("all")}
							>
								Show all issues
							</button>
						</div>
					</Col>
				</Row>

				{all && <Row style={{ marginTop: "5px" }}>
					<Col sm={3}>
						<p>Month Range Selection</p>
						<DatePicker
							swapRange
							disabled={!ready}
							selected={startDate}
							onChange={handleChange}
							selectsRange
							startDate={startDate}
							endDate={endDate}
							dateFormat="MM/yyyy"
							showMonthYearPicker
							maxDate={localTimeToPickerTime()}
							style={{ width: "100%" }}
							inputStyle={DatePickerInputStyles}
						/>
						<DatePickerInputStyles />

					</Col>
				</Row>}

				<Row style={{ marginTop: "5px" }}>
					{ready ? (
						<MaterialTable
							title={title}
							style={{ zIndex: 0 }} // to be displayed behind Select Value Container
							columns={cols}
							data={data}
							actions={tableAction}
							onRowClick={(e, rowData) => setClickedRow(rowData.tableData.id)}
							detailPanel={[
								(rowData) => ({
									disabled: rowData.actions.length < 2,
									icon: () => (
										<Icon
											style={{
												display:
													rowData.actions.length < 2 ? "none" : "inline-block",
											}}
										>
											chevron_right
										</Icon>
									),
									render: (rowData) => actionsDetailPanel(rowData),
								}),
							]}
							options={{
								actionsColumnIndex: -1,
								exportAllData: true,
								exportButton: true,
								exportFileName: "Issue_Report",
								onPageChange: true,
								headerStyle: { fontSize: "1.2rem", },
								searchFieldStyle: { fontSize: "1rem", },
								pageSize: 50,
								emptyRowsWhenPaging: false,
								pageSizeOptions: [50, 100, 200, `${data.length}`],
								rowStyle: (rowData) => ({
									backgroundColor:
										clickedRow === rowData.tableData.id ? "#F5DEB3" : "#FFF",
								}),
							}}
						/>
					) : (
						<LoaderGraph />
					)}
				</Row>

				{/* where form is open */}
				{openForm && (
					<div className="overlay translucent">
						<div
							id="graphContainer"
							style={{
								marginTop: "25px",
								maxHeight: "85vh",
								overflowY: "auto",
							}}
							className="fadeIn"
						>
							<IssueForm
								closeForm={() => setOpenForm(false)}
								id={formID}
								reloadReport={reloadReport}
							/>
						</div>
					</div>
				)}
			</Container>

			<Noti />
		</>
	);
}

export default IssueList;
